/**
 * slug → worker, for the Work surfaces (YAG-372).
 *
 * A work item carries `persona_slug` and nothing else about the worker, so the
 * list rows and the pane need a name and a mark to draw a chip. That is ONE
 * read of the directory the Workers surface already serves, shared by every
 * caller through a module-level promise: a list of forty rows must not become
 * forty fetches, and the pane must not refetch what the list just read.
 *
 * `useWorkerDirectory` (workers/useWorkers.ts) stays the Workers surface's own
 * hook — it also carries the spin-up and rename writes, which the Work surface
 * has no business holding.
 *
 * A slug the directory does not know resolves to null, and the surfaces draw no
 * chip. That is the honest render for a worker who has been retired from the
 * registry: the work still happened, we just cannot name who did it, and a
 * guessed name would be worse than none.
 */
import { useEffect, useMemo, useState } from "react";
import type {
  WorkerDirectoryResponse,
  WorkerEngagementSummary,
  WorkerSummary,
} from "@yagni/shared";
import { captureUiError } from "../sentry.js";

export type WorkerLookup = (slug: string | null | undefined) => WorkerSummary | null;

/**
 * The Work list's lookup: the slug read every case-file caller has, plus the
 * craft read the list needs to name a stage's owner (`PIPELINE_STAGE_CRAFT`).
 * `byCraft` is required, not optional: the one production source is
 * {@link useWorkerLookup}, and a fake that only has slugs wraps itself with
 * {@link stageOwnerLookup} rather than forcing a dead guard into the row.
 */
export interface StageOwnerLookup extends WorkerLookup {
  /**
   * The worker who carries `craft` for this workspace, or null when nobody
   * available does. `preferSlug` is the slug stamped on the item: when that
   * worker already carries the craft it wins, so a workspace with two
   * Builders credits the one that actually did the work.
   */
  byCraft(craft: string, preferSlug?: string | null): WorkerSummary | null;
  /**
   * False while the directory read is still in flight, so a reader that
   * filters by Agent waits instead of finding nobody. Absent reads as true.
   */
  ready?: boolean;
  /**
   * True when the directory read failed, so a reader filtering by Agent can
   * say why nothing matches instead of claiming there is nothing. Absent
   * reads as false.
   */
  failed?: boolean;
}

/** Build a {@link StageOwnerLookup} from a slug read and a craft read. */
export function stageOwnerLookup(
  bySlug: WorkerLookup,
  byCraft: StageOwnerLookup["byCraft"] = () => null,
): StageOwnerLookup {
  return Object.assign((slug: string | null | undefined) => bySlug(slug), { byCraft });
}

/**
 * Who carries a craft, from a directory read. The item's own worker wins when
 * it carries the craft, available or not: that worker did the work, and a
 * record is honest about who did it even after they were benched. Otherwise
 * the first AVAILABLE worker with the craft, in directory order: a benched or
 * guild entry ("render, never engage") is never GUESSED as a stage owner, the
 * same rule the backend registry applies when it resolves its builder and
 * reviewer.
 */
export function resolveWorkerByCraft(
  workers: Iterable<WorkerSummary>,
  craft: string,
  preferSlug?: string | null,
): WorkerSummary | null {
  let first: WorkerSummary | null = null;
  for (const worker of workers) {
    if (worker.craft !== craft) continue;
    if (preferSlug && worker.slug === preferSlug) return worker;
    if (!first && worker.available) first = worker;
  }
  return first;
}

/** One directory row: the worker plus every Team they serve on. */
export type DirectoryWorkerEntry = WorkerSummary & {
  engagements: WorkerEngagementSummary[];
};

/** Resolves to null on a failed read — failure is never memoized as "empty". */
let cache: Promise<Map<string, DirectoryWorkerEntry> | null> | null = null;

function readDirectory(): Promise<Map<string, DirectoryWorkerEntry> | null> {
  const request: Promise<Map<string, DirectoryWorkerEntry> | null> = fetch("/api/workers", {
    credentials: "include",
  })
    .then((res) => {
      if (!res.ok) throw new Error(`workers directory read failed (${res.status})`);
      return res.json() as Promise<Partial<WorkerDirectoryResponse>>;
    })
    .then((body) => {
      const map = new Map<string, DirectoryWorkerEntry>();
      for (const worker of body?.workers ?? []) {
        if (worker && typeof worker.slug === "string") {
          map.set(worker.slug, { ...worker, engagements: worker.engagements ?? [] });
        }
      }
      return map;
    })
    .catch((err: unknown) => {
      // One transient 500 must not empty every roster for the whole session:
      // drop the shared promise so the NEXT mount retries the read. This
      // mount reports the failure as an empty-but-settled, failed directory.
      if (cache === request) cache = null;
      captureUiError(err, { area: "Work", operation: "read_worker_directory", severity: "warning" });
      return null;
    });
  return request;
}

/** Drop the shared read (tests, and a directory rename that must take effect). */
export function resetWorkerLookupCache(): void {
  cache = null;
}

interface DirectoryState {
  workers: Map<string, DirectoryWorkerEntry>;
  /** True until the shared read settles — an empty map while loading means "not read yet", not "no workers". */
  loading: boolean;
  /** The read settled by failing: the empty map means "could not read", not "no workers". */
  failed: boolean;
}

function useDirectory(): DirectoryState {
  const [state, setState] = useState<DirectoryState>(() => ({
    workers: new Map(),
    loading: true,
    failed: false,
  }));

  useEffect(() => {
    let cancelled = false;
    const request = (cache ??= readDirectory());
    void request.then((map) => {
      if (!cancelled) setState({ workers: map ?? new Map(), loading: false, failed: map === null });
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}

/** Every directory row with its engagements, off the same one shared read. */
export function useWorkerDirectoryEntries(): { entries: DirectoryWorkerEntry[]; ready: boolean; failed: boolean } {
  const { workers, loading, failed } = useDirectory();
  return useMemo(() => ({ entries: Array.from(workers.values()), ready: !loading, failed }), [workers, loading, failed]);
}

export function useWorkerLookup(): StageOwnerLookup {
  const { workers, loading, failed } = useDirectory();
  // One identity per directory state, so the list's memos keyed on the
  // lookup recompute when the directory changes, not on every render.
  return useMemo(() => {
    const lookup = stageOwnerLookup(
      (slug) => (slug ? (workers.get(slug) ?? null) : null),
      (craft, preferSlug) => resolveWorkerByCraft(workers.values(), craft, preferSlug),
    );
    lookup.ready = !loading;
    lookup.failed = failed;
    return lookup;
  }, [workers, loading, failed]);
}

